/**
 * The tracked-account routes' typed edge (platform design §5.2, §5.4, §6.3.5, §6.3.9), written
 * field for field from `api/src/socialsrc_api/schemas/shell.py` (`TrackedAccountRowOut`,
 * `TrackedAccountIn`, `TagListOut`, `TrackedAccountLabelIn`, `TrackedAccountLabelOut`) and
 * `schemas/estimates.py` (`TrackingConfigIn`, `TrackingOut`, `TrackingWriteOut`) as they stand on `api` `origin/main`, rather than imported:
 * this is a `web`-owned contract.
 *
 * `label` is the tenant's own name for the account; `null` falls back to the handle at the
 * display edge (§5.4). `PUT .../tracked-accounts/{id}/label` is its own route, not a field on
 * `TrackingConfigIn`: a rename must not replace the tracking configuration or mark the account
 * for re-planning. The burn-rate payload is `control/api/schemas`'s `burnRateSchema`, which is
 * the same `BurnRateOut` transcription and is not written twice.
 */

import { z } from 'zod';

export { burnRateSchema, type BurnRate } from '../../../control/api/schemas';

export const PLATFORMS = ['instagram', 'tiktok'] as const;
export const platformSchema = z.enum(PLATFORMS);
export type Platform = z.infer<typeof platformSchema>;

export const mediaKindSchema = z.enum(['POST', 'REEL', 'STORY']);
export type MediaKind = z.infer<typeof mediaKindSchema>;

/** `auto` is meaningful for REEL only (§6.3.1). */
export const mediaEnablementSchema = z.enum(['on', 'off', 'auto']);
export type MediaEnablement = z.infer<typeof mediaEnablementSchema>;

export const ALIGNMENTS = ['CAMPAIGN', 'ALLY', 'NEUTRAL', 'ADVERSARY'] as const;
export const alignmentSchema = z.enum(ALIGNMENTS);
export type Alignment = (typeof ALIGNMENTS)[number];

export const BASES = ['CONSENT', 'PUBLIC'] as const;
export type Basis = (typeof BASES)[number];

export const INFLUENCE_SCOPES = [
  'neighborhood',
  'district',
  'municipality',
  'state',
  'country',
] as const;
export type InfluenceScope = (typeof INFLUENCE_SCOPES)[number];

/**
 * A named locality a registration's `places` resolves against -- searched at one
 * `InfluenceScope` level, never created. Written field for field from `api`'s
 * `GET /tenants/{t}/places` (`schemas/places.py`'s `PlaceOut`). `uf` is `null` at country level
 * (`ck_places_state_code_absent_only_at_country`), present at every level below it.
 */
export const placeSchema = z.object({
  id: z.string(),
  name: z.string(),
  uf: z.string().nullable(),
  // The parent place's name, the municipality a bairro sits in.
  parent_name: z.string().nullish(),
});
export type Place = z.infer<typeof placeSchema>;

export const placeListSchema = z.object({ items: z.array(placeSchema) });

/**
 * A campaign's tag vocabulary (`TagField`, frames 86:2, 304:11029, 632:19479 states 1-2, 5), the
 * same schema the tags screen reads (`tenant/tags/api/schemas.ts`). `active` hides a category
 * from the key step's list without unassigning it from an account that already carries one of
 * its values. A value carries no per-value account count -- only `TagCategoryOut.accounts`
 * counts distinct accounts across the whole category.
 */
export {
  tagCategorySchema,
  tagValueSchema,
  type TagCategory,
  type TagValue,
} from '../../../tenant/tags/api/schemas';

export { tagVocabularyResponseSchema as tagVocabularySchema } from '../../../tenant/tags/api/schemas';

/** A `(category, value)` pair as `TagField` tracks it: both names, for the chip label, plus the
 *  real vocabulary value's id -- `TrackedAccountRegistration.value_ids` sends the ids alone, which
 *  `api`'s `TrackedAccountIn.value_ids` receives. */
export interface TagPair {
  category: string;
  value: string;
  valueId: string;
  /** The lucide name the chip renders (`tenant/tags/icons.tsx`); absent renders no icon. */
  icon?: string | null;
}

export const trackedAccountRowSchema = z.object({
  id: z.uuid(),
  // null for an account known only by its platform user id.
  handle: z.string().nullable(),
  // The tenant's own name for the account. null falls back to the handle at the display edge (§5.4).
  label: z.string().nullable(),
  platform: platformSchema,
  // The account's stored profile picture (signed); null if never stored.
  avatar_url: z.string().nullable(),
  profile_id: z.uuid().nullable(),
  // The profile's slug, not its display name. null is a fully custom account (§6.3.5).
  profile_name: z.string().nullable(),
  enabled_kinds: z.array(mediaKindSchema),
  reels_in_feed: z.boolean().nullable(),
  tags: z.array(z.string()),
  // Every vocabulary value the account carries, alignment first, with the icon it renders with
  // (its own, else its category's) -- `TrackedAccountRowOut.account_tags`.
  account_tags: z
    .array(
      z.object({
        category: z.string(),
        value: z.string(),
        value_id: z.uuid(),
        icon: z.string().nullish(),
      }),
    )
    .optional(),
  // The alignment category's value the account carries; `alignment` below is its fixed key.
  alignment_value_id: z.uuid().nullish(),
  // The list's alignment grouping (105:6's three bands) -- `TrackedAccountRowOut.alignment`.
  // `.nullish()` stays: a fixture predating this field is missing the property outright, not
  // merely `null`.
  alignment: alignmentSchema.nullish(),
  // 105:6's two counts and the 7-day reach delta -- `TrackedAccountRowOut`'s own `reach`,
  // `publications` and `reach_delta_pct`.
  // `null` is an account with no readings in the window (renders "sem leitura"), never `0`.
  reach: z.number().int().nullish(),
  publications: z.number().int().nullish(),
  // A percentage-point change (20 means +20%), not a ratio -- `percentDelta` in `i18n/format.ts`
  // divides by 100 before Intl's percent style multiplies it back out.
  reach_delta_pct: z.number().nullish(),
  // Every own post published in the same trailing window, related or not.
  posts_collected: z.number().int().nullish(),
  // When the newest post was first stored; null before any collection found one.
  last_collected_at: z.string().nullish(),
  // Display figures off the shared account row: null is unknown, never 0 or public.
  followers: z.number().int().nullish(),
  is_private: z.boolean().nullish(),
  // The rest of the latest profile collection's reading, each null when unknown.
  display_name: z.string().nullish(),
  bio: z.string().nullish(),
  verified: z.boolean().nullish(),
  following: z.number().int().nullish(),
  posts_count: z.number().int().nullish(),
  profile_fetched_at: z.string().nullish(),
});
export type TrackedAccountRow = z.infer<typeof trackedAccountRowSchema>;

export const trackedAccountListSchema = z.object({ items: z.array(trackedAccountRowSchema) });

/**
 * `POST /campaigns/{c}/tracked-accounts/estimate` (`api`'s `NewAccountEstimateOut`, written
 * field for field rather than imported, same as the rest of this file): the add-account dialog's
 * footer preview (86:2, 304:11029), priced before the account exists. A named profile prices its
 * own catalogue figure; no profile prices whatever `media` names, `[]` at zero -- there is no
 * custom-media price for the custom mode's draft, so that is the only body the dialog sends.
 */
export const newAccountEstimateSchema = z.object({
  credits_per_day: z.number().nullable(),
  unpriced: z.array(z.string()),
});
export type NewAccountEstimate = z.infer<typeof newAccountEstimateSchema>;

export const tagListSchema = z.object({ items: z.array(z.string()) });

/**
 * `POST /campaigns/{c}/tracked-accounts`. `influence_scope` has no default in `api`, so the key
 * is required and `null` is the way to send none. Every account collects through a profile, named
 * by `profile_id`. `value_ids` are the campaign's tag
 * vocabulary values `TagField` picked, distinct from the flat strings `TrackedAccountRow.tags`
 * still reads back.
 */
export interface TrackedAccountRegistration {
  platform: Platform;
  handle: string;
  alignment: Alignment;
  // A value of the campaign's alignment category; `api` takes its key over `alignment`.
  alignment_value_id?: string;
  basis: Basis;
  value_ids?: string[];
  consent_ref?: string | null;
  influence_scope: InfluenceScope | null;
  // The chosen places' ids, at `influence_scope`'s level. Omitted, never `[]`, when none are
  // chosen, so a registration naming no locality looks the same on the wire as one predating
  // this field.
  places?: string[];
  profile_id: string;
}

/** The account's collection is its profile, whole. */
export const trackingSchema = z.object({
  profile_id: z.uuid().nullable(),
});
export type Tracking = z.infer<typeof trackingSchema>;

export const trackedAccountLabelSchema = z.object({
  tracked_account_id: z.uuid(),
  label: z.string().nullable(),
});
export type TrackedAccountLabel = z.infer<typeof trackedAccountLabelSchema>;

/** `PUT .../tracked-accounts/{id}/label`'s body: `null` clears the label. */
export interface TrackedAccountLabelWrite {
  label: string | null;
}

/** `ReplanTargetOut`: which `(account, media_kind)` targets a write resolved. */
const replanTargetSchema = z.object({
  tracked_account_id: z.uuid(),
  account_id: z.uuid(),
  media_kind: mediaKindSchema,
});

/** `ReplanOut`, shared by every write response that resolves re-plan targets
 *  (`TrackingWriteOut`, `BulkApplyOut`). `replan_recorded` is false until the
 *  durable re-plan table exists: the targets were resolved, not recorded. */
const replanSchema = z.object({
  replan_targets: z.array(replanTargetSchema),
  replan_recorded: z.boolean(),
});

export const trackingWriteSchema = trackingSchema.extend(replanSchema.shape);
export type TrackingWrite = z.infer<typeof trackingWriteSchema>;

export interface TrackingConfigWrite {
  profile_id: string;
}

/**
 * The account screen's five reading routes (`api`'s `routers/read_accounts.py`,
 * `schemas/read_accounts.py`, written field for field as they stand on `api` `origin/main`
 * rather than imported, same as the rest of this file). Every route takes the window as
 * required `from`/`to` query parameters (`deps_read.py`'s `WindowDep`) -- there is no default.
 */

export const accountKpisSchema = z.object({
  // Sum of the window's daily reach, related posts only. null when nothing was read.
  reach: z.number().int().nullable(),
  // Related posts in the window.
  publications: z.number().int().nullable(),
  // The fraction (0..1) of the account's own output in the window that counted as related.
  related_share: z.number().nullable(),
  // Net of the three sentiment buckets over the ones actually scored, null when nothing was.
  sentiment: z.number().nullable(),
});
export type AccountKpis = z.infer<typeof accountKpisSchema>;

const dailyReachSchema = z.object({ day: z.string(), value: z.number().int() });

export const accountReachSeriesSchema = z.object({
  // Sorted by day; a day with no reading is omitted, never a zero-value entry.
  days: z.array(dailyReachSchema),
});
export type AccountReachSeries = z.infer<typeof accountReachSeriesSchema>;

const mediaItemSchema = z.object({
  ordinal: z.number().int(),
  media_kind: z.enum(['image', 'video']),
  source_url: z.string().nullable(),
  pending: z.boolean(),
  poster_url: z.string().nullable(),
  duration_seconds: z.number().nullable(),
});

const coauthorSchema = z.object({
  account_id: z.uuid(),
  handle: z.string().nullable(),
  avatar_url: z.string().nullable(),
});

export const postRowSchema = z.object({
  post_id: z.uuid(),
  account_id: z.uuid(),
  handle: z.string().nullable(),
  label: z.string().nullable(),
  platform: z.string(),
  kind: z.string(),
  published_at: z.string(),
  url: z.string().nullable(),
  caption: z.string().nullable(),
  deleted_at: z.string().nullable(),
  media: z.array(mediaItemSchema),
  avatar_url: z.string().nullable(),
  coauthors: z.array(coauthorSchema),
});
export type PostRow = z.infer<typeof postRowSchema>;

export const accountPublicationsSchema = z.object({
  related: z.array(postRowSchema),
  all_posts: z.array(postRowSchema),
});
export type AccountPublications = z.infer<typeof accountPublicationsSchema>;

export const accountSentimentSchema = z.object({
  // Entitled to the tenant, scored or not. null when nothing was entitled in the window.
  collected: z.number().int().nullable(),
  // The subset of `collected` that has been scored.
  read: z.number().int().nullable(),
  apoiam: z.number().int().nullable(),
  neutros: z.number().int().nullable(),
  criticam: z.number().int().nullable(),
});
export type AccountSentiment = z.infer<typeof accountSentimentSchema>;

const topicLeafSchema = z.object({ theme: z.string(), count: z.number().int() });
/** A Tema/Pauta value and the children posts were placed under, each counted in posts. */
const topicCountSchema = topicLeafSchema.extend({
  children: z.array(topicLeafSchema).default([]),
});

export const accountTopicsSchema = z.object({
  // The same denominator AccountKpis.publications reports.
  related_posts: z.number().int().nullable(),
  // Highest count first.
  topics: z.array(topicCountSchema),
});
export type AccountTopics = z.infer<typeof accountTopicsSchema>;
